import React, { useRef, useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { IoClose } from 'react-icons/io5';
import { VscChromeMinimize } from 'react-icons/vsc';
import { MdOutlineOpenInFull, MdOutlineCloseFullscreen } from 'react-icons/md';
import { useHelp } from './HelpContext';
import useViewport from '../../hooks/useViewport';
import './Help.css';

const PLAYER_W = 360;
const PLAYER_H = 202; // 16:9 ratio

const FloatingVideoPlayer = () => {
  const { activeVideo, isMinimized, isFullscreen, closeVideo, minimizeVideo, maximizeVideo, toggleFullscreen } = useHelp();

  const [pos, setPos] = useState({ x: null, y: null }); // null = use CSS default (bottom-right)
  const isDragging = useRef(false);
  const dragStart = useRef({ mouseX: 0, mouseY: 0, posX: 0, posY: 0 });
  const playerRef = useRef(null);
  const { isPhone: isMobile } = useViewport();

  // Reset position when video changes
  useEffect(() => {
    setPos({ x: null, y: null });
  }, [activeVideo?.id]);

  // Pointer events (not mouse events) so the player can also be dragged by finger.
  const onPointerDown = useCallback((e) => {
    if (isMinimized || isMobile || isFullscreen) return;
    e.preventDefault();
    isDragging.current = true;
    const rect = playerRef.current.getBoundingClientRect();
    dragStart.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      posX: rect.left,
      posY: rect.top,
    };

    const onPointerMove = (e) => {
      if (!isDragging.current) return;
      const dx = e.clientX - dragStart.current.mouseX;
      const dy = e.clientY - dragStart.current.mouseY;
      const newX = dragStart.current.posX + dx;
      const newY = dragStart.current.posY + dy;
      // Clamp within viewport
      const maxX = window.innerWidth - PLAYER_W;
      const maxY = window.innerHeight - (isMinimized ? 44 : PLAYER_H + 44);
      setPos({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    };

    const onPointerUp = () => {
      isDragging.current = false;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
    };

    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('pointerup', onPointerUp);
    document.addEventListener('pointercancel', onPointerUp);
  }, [isMinimized, isMobile, isFullscreen]);

  if (!activeVideo) return null;

  const embedUrl = `https://www.youtube.com/embed/${activeVideo.id}?autoplay=1&rel=0&modestbranding=1`;

  // Fullscreen modal — portal to body
  if (isFullscreen) {
    return ReactDOM.createPortal(
      <div className="help-fullscreen-overlay" onClick={(e) => { if (e.target === e.currentTarget) toggleFullscreen(); }}>
        <div className="help-fullscreen-modal">
          <div className="help-fullscreen-header">
            <span className="help-player-title">{activeVideo.title}</span>
            <div className="help-player-controls">
              <button className="help-ctrl-btn" onClick={toggleFullscreen} title="Exit fullscreen">
                <MdOutlineCloseFullscreen size={16} />
              </button>
              <button className="help-ctrl-btn help-ctrl-close" onClick={closeVideo} title="Close">
                <IoClose size={16} />
              </button>
            </div>
          </div>
          <iframe
            src={embedUrl}
            title={activeVideo.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="help-fullscreen-iframe"
          />
        </div>
      </div>,
      document.body
    );
  }

  // Compute inline style for position
  const floatStyle = pos.x !== null
    ? { left: pos.x, top: pos.y, bottom: 'auto', right: 'auto' }
    : { bottom: 24, right: 24 };

  // Minimized pill
  if (isMinimized) {
    return ReactDOM.createPortal(
      <div
        className="help-player-minimized"
        style={{ ...floatStyle, position: 'fixed', zIndex: 9999 }}
      >
        <div className="help-player-mini-content">
          <span className="help-player-mini-title">{activeVideo.title}</span>
          <div className="help-player-controls">
            <button className="help-ctrl-btn" onClick={maximizeVideo} title="Restore">
              <MdOutlineOpenInFull size={14} />
            </button>
            <button className="help-ctrl-btn help-ctrl-close" onClick={closeVideo} title="Close">
              <IoClose size={14} />
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Normal floating player
  return ReactDOM.createPortal(
    <div
      ref={playerRef}
      className={`help-player${isMobile ? ' help-player--mobile' : ''}`}
      style={{ ...floatStyle, position: 'fixed', zIndex: 9999 }}
    >
      {/* Drag handle / title bar */}
      <div
        className="help-player-header"
        onPointerDown={onPointerDown}
        style={{ cursor: isMobile ? 'default' : 'grab', touchAction: 'none' }}
      >
        <span className="help-player-title" title={activeVideo.title}>
          {activeVideo.title}
        </span>
        <div className="help-player-controls">
          <button className="help-ctrl-btn" onClick={minimizeVideo} title="Minimize">
            <VscChromeMinimize size={14} />
          </button>
          <button className="help-ctrl-btn" onClick={toggleFullscreen} title="Fullscreen">
            <MdOutlineOpenInFull size={14} />
          </button>
          <button className="help-ctrl-btn help-ctrl-close" onClick={closeVideo} title="Close">
            <IoClose size={14} />
          </button>
        </div>
      </div>

      {/* YouTube iframe */}
      <iframe
        src={embedUrl}
        title={activeVideo.title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="help-player-iframe"
      />
    </div>,
    document.body
  );
};

export default FloatingVideoPlayer;
